import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Loader2, ArrowLeft, CalendarX2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function CancelPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [state, setState] = useState("loading"); // loading | ready | cancelling | done | error
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Link non valido: manca il codice della prenotazione.");
      setState("error");
      return;
    }
    api.get("/bookings/lookup", { params: { token } })
      .then(({ data }) => {
        setBooking(data);
        setState("ready");
      })
      .catch((err) => {
        setError(formatApiError(err, "Prenotazione non trovata o già annullata."));
        setState("error");
      });
  }, [token]);

  const confirmCancel = async () => {
    setState("cancelling");
    try {
      await api.post("/bookings/cancel", { token });
      setState("done");
      toast.success("Prenotazione annullata.");
    } catch (err) {
      setError(formatApiError(err, "Annullamento non riuscito."));
      setState("error");
    }
  };

  return (
    <div className="min-h-screen bg-void flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute -top-32 left-1/3 w-[500px] h-[400px] bg-ember/10 blur-[130px] rounded-full pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md bg-card border border-white/[0.08] rounded-lg p-8 relative"
        data-testid="cancel-page-card"
      >
        <img src="/logo.png" alt="Modico Barberia" className="h-14 w-auto object-contain" />

        {state === "loading" && (
          <div className="mt-8 flex justify-center py-6">
            <Loader2 className="animate-spin text-ember" size={26} />
          </div>
        )}

        {state === "error" && (
          <>
            <AlertCircle size={36} className="mt-6 text-red-400" />
            <h1 className="mt-4 font-serif text-3xl text-ivory">Qualcosa non va</h1>
            <p data-testid="cancel-error-text" className="mt-2 text-sm text-sand leading-relaxed">{error}</p>
          </>
        )}

        {(state === "ready" || state === "cancelling") && booking && (
          <>
            <CalendarX2 size={36} className="mt-6 text-ember" />
            <h1 className="mt-4 font-serif text-3xl text-ivory">Annullare l'appuntamento?</h1>
            <div className="mt-5 bg-espresso border border-white/[0.08] rounded-md divide-y divide-white/[0.06]">
              {[
                ["Servizio", booking.service_name],
                ["Giorno", format(new Date(booking.date + "T00:00:00"), "EEEE d MMMM yyyy", { locale: it })],
                ["Ora", `${booking.time} (${booking.duration || 60} min)`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between items-center px-5 py-3.5">
                  <span className="text-xs font-mono uppercase tracking-widest text-stone">{k}</span>
                  <span className="text-sm text-ivory capitalize">{v}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-stone leading-relaxed">
              Annullando, la fascia torna subito disponibile per un altro cliente e Daniele riceve una notifica.
            </p>
            {booking.cancellable === false ? (
              <div data-testid="cancel-cutoff-notice" className="mt-6 bg-espresso border border-ember/30 rounded-md p-5">
                <p className="text-sm text-sand leading-relaxed">
                  La disdetta online è possibile fino a <span className="text-ember font-medium">12 ore prima</span> dell'appuntamento.
                  Per urgenze chiama Daniele al{" "}
                  <a href="tel:+393391470281" className="text-ember hover:text-ember-hover underline underline-offset-2">339 147 0281</a>.
                </p>
              </div>
            ) : (
              <button
                data-testid="cancel-confirm-button"
                onClick={confirmCancel}
                disabled={state === "cancelling"}
                className="mt-6 w-full py-3.5 bg-red-500/90 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-mono uppercase tracking-widest rounded-sm transition-colors duration-300 flex items-center justify-center gap-2"
              >
                {state === "cancelling" && <Loader2 size={15} className="animate-spin" />}
                Sì, annulla la prenotazione
              </button>
            )}
          </>
        )}

        {state === "done" && (
          <>
            <CheckCircle2 size={36} className="mt-6 text-ember" />
            <h1 className="mt-4 font-serif text-3xl text-ivory">Prenotazione annullata</h1>
            <p data-testid="cancel-success-text" className="mt-2 text-sm text-sand leading-relaxed">
              La fascia è stata liberata. Speriamo di rivederti presto in bottega!
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="cancel-back-home"
          className="mt-8 flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-widest text-stone hover:text-sand transition-colors duration-300"
        >
          <ArrowLeft size={13} /> Torna al sito
        </Link>
      </motion.div>
    </div>
  );
}
